import {
  Box,
  Flex,
  FormControl,
  Heading,
  HStack,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { HiOutlineDocumentSearch } from "react-icons/hi";

import {
  LoadingIndicator,
  SearchInput,
  Select,
  useToast,
} from "../../../../components";
import { EmptyStateWithIcon } from "../../../../components/EmptyStates";
import useJbpOffsetPagination from "../../../../hooks/useJbpOffsetPagination";
import {
  PositionJbpListItemFragment,
  PositionPageListItemFragment,
  useClientsQuery,
  useInterviewLocationsQuery,
  useOrgPositionsJbpLazyQuery,
  useUpdateClientAutoJoinMutation,
  useUpdateInterviewLocationAutoJoinMutation,
  useUpdatePositionsRecordingSettingsMutation,
  useUpdateRecordingSettingsAllNewPositionsMutation,
} from "../../../graphql";
import RecordingSettingsAllFutureList, {
  DisplayData,
} from "./RecordingSettingsAllFutureList";
import RecordingSettingsAllFutureOrg from "./RecordingSettingsAllFutureOrg";
import RecordingSettingsList from "./RecordingSettingsList";
import RecordingSettingsPopover from "./RecordingSettingsPopover";

const RecordingSettings: React.FC = () => {
  const toast = useToast();

  const { data: interviewLocationsData } = useInterviewLocationsQuery();
  const { data: clientsData } = useClientsQuery();

  const onSearch: (searchString: string) => void = (searchString) => {
    setSearchTerm(searchString);
  };

  const onClientSelect: (id: string) => void = (id) => {
    setClientId(id);
  };

  const onLocationSelect: (id: string) => void = (id) => {
    setLocationId(id);
  };

  const [fetchJBP, jbpData] = useOrgPositionsJbpLazyQuery({
    variables: {
      atsOnly: true,
    },
    onError: (err) => {
      toast({
        title: "Error",
        description: `Failed to load positions: ${err.message}`,
        status: "error",
      });
    },
    fetchPolicy: "network-only",
    onCompleted: ({ currentUser }) => {
      if (!currentUser) return;
      const list = currentUser.organization.jbpPositions || [];
      setNumberOfPages(
        currentUser.organization.jbpPositions.pageInfo?.totalPages || 1
      );
      setListItems(list.results ?? []);
    },
  });

  const {
    listItems: positions,
    setListItems,
    setNumberOfPages,
    pageOptions,
    searchTerm,
    clientId,
    locationId,
    setSearchTerm,
    setClientId,
    setLocationId,
    didFetchComplete,
    reload,
  } = useJbpOffsetPagination<PositionPageListItemFragment>(
    (opts) =>
      fetchJBP({
        ...opts,
        variables: {
          atsOnly: true,
          ...opts?.variables,
        },
      }),
    {
      initialSortBy: { id: "client.name", desc: true },
      pageLimit: 100,
      scrollToTop: false,
    }
  );

  const [updatePositionsRecordingSettings] =
    useUpdatePositionsRecordingSettingsMutation({
      onError: (err) => {
        toast({
          status: "error",
          title: "Error",
          description: `Failed to update recording settings: ${err.message}`,
        });
      },
      onCompleted: (data) => {
        if (
          data?.updatePositionsRecordingSettings &&
          data?.updatePositionsRecordingSettings.positions.length > 0
        ) {
          toast({
            title: "Success",
            description: `Successfully updated recording settings`,
            status: "success",
          });
          reload();
        }
      },
    });

  const handleUpdateOrgSettingsAutoJoin = (
    autoJoinInterview: boolean,
    atsOptInInterview: boolean
  ): Promise<any> => {
    return updateOrganizationAllNewPositions({
      variables: {
        autoJoinAllNewPositions: autoJoinInterview,
        atsOptInAllNewPositions: atsOptInInterview,
      },
    });
  };

  const [updateOrganizationAllNewPositions] =
    useUpdateRecordingSettingsAllNewPositionsMutation({
      onError: (err) => {
        toast({
          status: "error",
          title: "Error",
          description: `Failed to update recording settings for new positions: ${err.message}`,
        });
      },
      onCompleted: (data) => {
        toast({
          title: "Success",
          description: "Updated recording settings for all new positions",
          status: "success",
        });
      },
    });

  const handleUpdatePositionsRecordingSettings = (
    positions: Array<PositionJbpListItemFragment>,
    autoJoinInterview: boolean,
    atsOptInInterview: boolean
  ): Promise<any> => {
    const positionIds = positions.map((position) => position.id);
    return updatePositionsRecordingSettings({
      variables: { positionIds, autoJoinInterview, atsOptInInterview },
    });
  };

  const [updateInterviewLocationAutoJoin] =
    useUpdateInterviewLocationAutoJoinMutation({
      onError: (err) => {
        toast({
          status: "error",
          title: "Error",
          description: `Failed to turn on Auto Join New Positions: ${err.message}`,
        });
      },
      onCompleted: (data) => {
        if (
          data?.updateInterviewLocationAutoJoin &&
          data?.updateInterviewLocationAutoJoin.interviewLocations.length > 0
        ) {
          toast({
            title: "Success",
            description: `Successfully updated recording settings for the ${data?.updateInterviewLocationAutoJoin.interviewLocations[0].name} location`,
            status: "success",
          });
        }
      },
    });

  const handleUpdateLocationAutoJoin = (
    lcs: DisplayData[],
    autoJoinInterview: boolean,
    optIn: boolean
  ): Promise<any> => {
    const interviewLocationIds = lcs.map((lc) => lc.id);
    return updateInterviewLocationAutoJoin({
      variables: { interviewLocationIds, autoJoinInterview, optIn },
    });
  };

  const [updateClientAutoJoin] = useUpdateClientAutoJoinMutation({
    onError: (err) => {
      toast({
        status: "error",
        title: "Error",
        description: `Failed to turn on Auto Join New Positions: ${err.message}`,
      });
    },
    onCompleted: (data) => {
      if (
        data?.updateClientAutoJoin &&
        data?.updateClientAutoJoin.clients.length > 0
      ) {
        toast({
          title: "Success",
          description: `Successfully updated recording settings for the ${data?.updateClientAutoJoin.clients[0].name} position group`,
          status: "success",
        });
      }
    },
  });

  const handleUpdateClientAutoJoin = (
    lcs: DisplayData[],
    autoJoinInterview: boolean,
    optIn: boolean
  ): Promise<any> => {
    const clientIds = lcs.map((lc) => lc.id);
    return updateClientAutoJoin({
      variables: { clientIds, autoJoinInterview, optIn },
    });
  };

  const atsOptInEnabled =
    jbpData.data?.currentUser?.organization?.atsOptInEnabled ?? false;

  const descriptiveText = `BrightHire will automatically join and record all video interviews for
        the positions selected below. ${
          atsOptInEnabled
            ? "Recording is also supported through explicit opt-ins based on position, where candidates must explicitly consent to being recorded in order for the interview to be captured."
            : ""
        } Candidates who have opted out and users who
        are excluded from recording will never be recorded.`;

  const selectedClient = clientsData?.clients?.find(
    (c: { id: string }) => c.id === clientId
  );
  const selectedLocation = interviewLocationsData?.interviewLocations?.find(
    (c: { id: string }) => c.id === locationId
  );

  return (
    <Box w="fit-content" mt="4">
      <Heading as="h1" size="md" mb="4">
        Recording Settings
      </Heading>
      <Text mb="8">{descriptiveText}</Text>
      <Box>
        <Flex
          alignItems="left"
          mb={5}
          flexFlow="column nowrap"
          id="search-filter--search"
        >
          <SearchInput
            placeholder="Search positions by name, requisition id, or department"
            defaultValue={searchTerm}
            onSearch={onSearch}
            width="508px"
            maxWidth="508px"
          />
          <Flex flexFlow="row nowrap" justifyContent="flex-start">
            <FormControl mt="2" flex="1">
              <Select
                ref={null}
                options={
                  clientsData?.clients?.map(
                    (r: { id: string; name: string }) => ({
                      value: r.id,
                      label: r.name,
                    })
                  ) || []
                }
                placeholder="All Departments"
                isDisabled={!!locationId}
                value={
                  selectedClient && {
                    value: selectedClient.id,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    label: selectedClient.name,
                  }
                }
                onChange={(selected: { value: string } | null) =>
                  onClientSelect(selected ? selected.value : "")
                }
                isClearable
                styles={{
                  container: (base: Record<string, unknown>) => ({
                    ...base,
                    width: 250,
                    maxHeight: 200,
                  }),
                }}
              />
            </FormControl>
            <FormControl mt="2" ml="2">
              <Select
                ref={null}
                options={
                  interviewLocationsData?.interviewLocations?.map(
                    (r: { id: string; name: string }) => ({
                      value: r.id,
                      label: r.name,
                    })
                  ) || []
                }
                placeholder="All Locations"
                isDisabled={!!clientId}
                value={
                  selectedLocation && {
                    value: selectedLocation.id,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    label: selectedLocation.name,
                  }
                }
                onChange={(selected: { value: string } | null) =>
                  onLocationSelect(selected ? selected.value : "")
                }
                isClearable
                styles={{
                  container: (base: Record<string, unknown>) => ({
                    ...base,
                    width: 250,
                    maxHeight: 200,
                  }),
                }}
              />
            </FormControl>
          </Flex>
        </Flex>
      </Box>
      <>
        {!locationId && !clientId && (
          <RecordingSettingsAllFutureOrg
            displayData={[
              {
                autoJoinAllNewPositions:
                  jbpData.data?.currentUser?.organization
                    ?.autoJoinAllNewPositions ?? false,
                atsOptInAllNewPositions:
                  jbpData.data?.currentUser?.organization
                    ?.atsOptInAllNewPositions ?? false,
              },
            ]}
            updatePositionsRecordingSettings={handleUpdateOrgSettingsAutoJoin}
            showOptIn={atsOptInEnabled}
          />
        )}
        {locationId && !clientId && (
          <RecordingSettingsAllFutureList
            displayData={
              interviewLocationsData?.interviewLocations.filter(
                (l) => l.id === locationId
              ) || []
            }
            updatePositionsRecordingSettings={handleUpdateLocationAutoJoin}
            showOptIn={atsOptInEnabled}
          />
        )}
        {clientId && !locationId && (
          <RecordingSettingsAllFutureList
            displayData={
              clientsData?.clients.filter((l) => l.id === clientId) || []
            }
            updatePositionsRecordingSettings={handleUpdateClientAutoJoin}
            showOptIn={atsOptInEnabled}
          />
        )}

        {positions.length > 0 && (
          <>
            <HStack mb="3">
              <RecordingSettingsPopover
                searchTerm={searchTerm}
                disabled={!locationId && !clientId && !searchTerm}
                locationId={locationId}
                clientId={clientId}
                positionsCount={positions.length}
                showOptIn={atsOptInEnabled}
                isInRow={false}
                onSettingsUpdated={reload}
              />
            </HStack>
            <RecordingSettingsList
              positions={positions}
              updatePositionsRecordingSettings={
                handleUpdatePositionsRecordingSettings
              }
              pageOptions={pageOptions}
              sortBy={pageOptions.sortBy}
              showOptIn={atsOptInEnabled}
              loading={jbpData.loading}
            />
          </>
        )}
        {positions.length === 0 && (
          <>
            {jbpData.loading ? (
              <LoadingIndicator />
            ) : (
              didFetchComplete && (
                <EmptyStateWithIcon
                  maxWidth="1200px"
                  heading="No positions found"
                  icon={HiOutlineDocumentSearch}
                  Text={() => (
                    <Box>
                      No positions were found{" "}
                      {searchTerm
                        ? " that match your query. Please try something else."
                        : " for this organization."}
                    </Box>
                  )}
                  padded={false}
                />
              )
            )}
          </>
        )}
      </>
    </Box>
  );
};

export default RecordingSettings;
